/**
 * ENG-4580: manager-side runtime for the Claude Code OAuth pairing flow.
 *
 * These functions own the actual tmux dance — sending `/login`,
 * polling the pane until Claude Code prints the OAuth URL, sending
 * the auth code, and detecting success/failure. They are the
 * counterpart to the pure parser in `claude-pair-parser.ts`.
 *
 * The API surface in ENG-4581 will wrap these — they don't include
 * any HTTP / DB code so the unit tests can target the parser layer
 * without spinning up a fake API. Errors are classified into the
 * `SessionError` shape so the API can translate them into structured
 * 4xx responses (e.g. `session_missing`).
 *
 * Architectural note: tmux capture-pane on a session that doesn't
 * exist exits non-zero with `can't find session`. Same goes for tmux
 * not being installed. classifyTmuxError covers both.
 */

import { execFile } from 'node:child_process';
import { promisify } from 'node:util';
// ENG-10862: `readFileSync` so finalize can tell a written config from a torn
// one — an mtime that advances onto unparseable JSON is not a success.
import { existsSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';
import { homedir, userInfo } from 'node:os';
import {
  claudeConfigDir as claudeConfigDirFor,
  claudeJsonPath as claudeJsonPathFor,
  effectiveClaudeJsonPath,
  inspectClaudeConfig,
  CLAUDE_CONFIG_DIR_ENV,
} from './claude-config-location.js';

import {
  classifyTmuxError,
  detectAuthOutcome,
  extractOAuthUrl,
  isUrlPromptReady,
  type AuthOutcome,
  type SessionError,
} from './claude-pair-parser.js';
import { consumerTermsOffKeys, selectRowKeys } from './claude-dialogs.js';

const execFileAsync = promisify(execFile);

// Pair-scoped tmux session name. The pairing flow runs inside a
// throwaway `claude` instance — never inside an agent's persistent
// session — so first-time auth on a fresh host works (no chicken-and-
// egg) and re-auth doesn't disrupt an in-flight agent conversation.
export function pairTmuxSession(pairId: string): string {
  return `agt-pair-${pairId.slice(0, 12)}`;
}

// ---------------------------------------------------------------------------
// Low-level helpers
// ---------------------------------------------------------------------------

interface CapturePaneOpts {
  /** How many lines of scrollback to include (negative = lines back). Default -200. */
  scrollback?: number;
}

async function capturePane(session: string, opts: CapturePaneOpts = {}): Promise<string> {
  const scrollback = opts.scrollback ?? -200;
  const { stdout } = await execFileAsync('tmux', [
    'capture-pane',
    '-t',
    session,
    '-p',
    '-S',
    String(scrollback),
  ]);
  return stdout;
}

async function sendKeys(session: string, ...keys: string[]): Promise<void> {
  await execFileAsync('tmux', ['send-keys', '-t', session, ...keys]);
}

async function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * ENG-10619: answer Claude Code's consumer-terms dialog with
 * "Help improve our AI models: OFF" if it is focused. Returns true when
 * keys were sent. One send-keys call per key with a beat between them —
 * batching arrow keys into one call risks a bracketed paste (see
 * sendDialogKeys in claude-dialogs.ts).
 */
async function answerConsumerTermsDialog(session: string, pane: string): Promise<boolean> {
  const keys = consumerTermsOffKeys(pane);
  if (!keys) return false;
  await sendKeySequence(session, keys);
  return true;
}

async function sendKeySequence(session: string, keys: readonly string[]): Promise<void> {
  for (let i = 0; i < keys.length; i++) {
    if (i > 0) await sleep(300);
    await sendKeys(session, keys[i]!);
  }
}

/**
 * ENG-10653: how long to wait, after a pane goes dead, for tmux to publish its
 * exit status. A wall-clock deadline, deliberately not an iteration count —
 * see the bound's derivation below for why that distinction is not cosmetic.
 *
 * MEASURED (`scripts/measure-tmux-dead-status.mjs`, macOS 15, tmux 3.7b,
 * 2026-09-18, across three load regimes — unloaded, 12-way CPU saturation and
 * a fork storm): **not once** did a poll see the `Pane is dead (...)` banner
 * drawn while `pane_dead_status` was still empty.
 *
 * That is stated as a count of a single-instant observation, deliberately, and
 * not as a timing gap. An earlier draft of this comment claimed "gap 0 ms at
 * p100"; that was an artefact of polling — two values read in the same poll
 * bound the gap by one poll width (~9–15 ms here), they do not measure it as
 * zero — and the companion claim that the banner "followed by 3–24 ms" was
 * just the cost of the second tmux call in the same iteration. Both are gone.
 *
 * Two consequences, and the second is why ENG-10631 did not close this:
 *
 *  1. The banner is not an independent source of the status. tmux draws it
 *     only once it has reaped the child, so the state ENG-10631's fallback
 *     exists to exploit — banner present, status absent — is one this tmux
 *     never actually entered. The fallback is still right and stays (it
 *     recovers a status the format read itself lost), but it could not win the
 *     reap race it was written for, which is why ENG-10643's queue run still
 *     read "exit status unknown" with all three of its fixes in the tree.
 *  2. The banner IS a completion marker. Once drawn, tmux has published
 *     everything it is going to, so it ends the wait as definitively as a
 *     numeric status does.
 *
 * The BOUND is therefore not derived from that measurement — a 0 ms gap would
 * justify no wait at all. It is derived from the CI tail, the only place the
 * gap has ever opened. ENG-10631 measured >1 s. The ENG-10643 ejection (run
 * 35223527037, job `tests (packages)`) then exhausted the 30 × 100 ms loop
 * that replaced it: the test took 4624 ms, which decomposes to the 500 ms
 * spawn settle + 3000 ms of sleeps + ~33 tmux client spawns at ~35 ms each.
 * That is the point about wall clock — the loop's nominal "3 s" was really
 * ~4.1 s, because each iteration also paid to spawn a tmux client, and the
 * number in the code did not say so.
 *
 * 15 s is ~3.7× the bound that was observed losing. It is affordable because
 * this wait is on a TERMINAL path — the pane is dead, the pair flow is over —
 * and it sits inside a single poll of callers budgeting 60 s
 * (`startClaudePair`) and 30 s (`submitClaudePairCode`). An alive pane never
 * reaches it.
 *
 * ENG-10714 — "sits inside" is true of the DEFAULT budgets and not of the
 * mechanism, so read it as a statement about today's callers rather than a
 * bound this wait respects. `checkPairPaneAlive` is called from INSIDE
 * `startClaudePair`'s poll loop, and that loop tests its deadline only
 * BETWEEN iterations — so one poll can overrun a caller's whole budget by up
 * to `DEAD_PANE_STATUS_WAIT_MS`, and the caller has no way to cut it short.
 * Measured 2026-09-19: a caller passing `timeoutMs: 10_000` returned after
 * 15050 ms. Nothing is broken by that today (the path is terminal and the
 * overrun buys the answer the caller wanted), but a future caller with a
 * genuinely tight budget needs `waitMs` threading through
 * `DeadPaneWaitOpts` — which `checkPairPaneAlive` already accepts and no
 * caller yet passes — rather than a smaller global bound. Raising this
 * constant without that is what would turn the overrun into a real one.
 */
export const DEAD_PANE_STATUS_WAIT_MS = 15_000;

/** One `#{pane_dead}|#{pane_dead_status}|#{pane_dead_signal}` reading. */
export interface DeadPaneReading {
  dead: string;
  exitStatus: string;
  /** Empty string on tmux builds with no `pane_dead_signal` format. */
  signal: string;
}

export interface DeadPaneWaitOpts {
  waitMs?: number;
  pollMs?: number;
  /** How often to spend a capture-pane looking for the completion banner. */
  captureEveryMs?: number;
  /**
   * ENG-10714: how long the FINAL capture may keep retrying while the dead
   * pane still shows nothing. Bounded and only entered when there is nothing
   * to show — a pane that already has content costs one capture, as before.
   */
  captureRetryMs?: number;
  /** Gap between those retries. */
  captureRetryPollMs?: number;
}

export interface DeadPaneVerdict {
  exitStatus: string;
  pane: string;
  /**
   * How the wait ended. Four of these are answers; only `timeout` means tmux
   * never told us. `signal` is a genuinely status-less death, not a missing
   * status — the distinction the operator message depends on.
   */
  resolved: 'status' | 'signal' | 'banner' | 'session-gone' | 'timeout';
  waitedMs: number;
  polls: number;
  readFailures: number;
  /**
   * ENG-10714: transient `capture-pane` failures. These used to be swallowed
   * by a bare `catch {}`, which made "the capture failed" and "claude printed
   * nothing" the same observable — so the message could not tell the operator
   * which had happened, and the after-window could not tell which was fixed.
   */
  captureFailures: number;
  /** ENG-10714: extra captures spent waiting for a dead pane to show content. */
  recaptures: number;
  /** Filled in only on `timeout`, where it is worth knowing. */
  tmuxVersion?: string;
}

/**
 * The pane lines that are claude's OUTPUT — everything except tmux's own
 * dead-pane banner and blank padding.
 *
 * Shared by the capture retry and the operator message deliberately: they are
 * answering the same question ("is there anything to show?") and a second copy
 * of the filter would let them disagree about it, so the retry could give up
 * on a pane the message then renders, or keep retrying one it would discard.
 */
function paneOutputLines(pane: string): string[] {
  return pane
    .split('\n')
    .map((l) => l.trimEnd())
    .filter((l) => l.length > 0 && !/^Pane is dead\b/.test(l));
}

/**
 * ENG-10653: wait for tmux to publish a dead pane's exit status, and report
 * how the wait ended.
 *
 * tmux marks a pane dead when the pty hits EOF but only fills in
 * `pane_dead_status` once it has reaped the child — separate events, so a read
 * in between sees a dead pane with an EMPTY status. Ending the wait on the
 * first of those and reporting "unknown" is the defect this closes.
 *
 * The I/O is injected so the wait can be proven against a fake that publishes
 * late or fails transiently. It cannot be proven against real tmux on a
 * developer machine: the gap there is 0 ms (see `DEAD_PANE_STATUS_WAIT_MS`),
 * so the race simply does not occur.
 *
 * ENG-10714: it DOES occur in CI, and harder than this comment implied — on
 * tmux 3.4 the full 15 s bound has been seen to expire with no status, no
 * signal and no banner, i.e. tmux published nothing at all rather than
 * publishing late. So the real-tmux test above asserts only what does not
 * depend on the race; anything that asserts the status is recovered belongs
 * against the injected fake, where the timing is the test's to choose.
 */
export async function awaitDeadPaneVerdict(
  io: { read: () => Promise<DeadPaneReading>; capture: () => Promise<string> },
  initial: DeadPaneReading,
  opts: DeadPaneWaitOpts = {},
): Promise<DeadPaneVerdict> {
  const waitMs = opts.waitMs ?? DEAD_PANE_STATUS_WAIT_MS;
  const pollMs = opts.pollMs ?? 100;
  const captureEveryMs = opts.captureEveryMs ?? 1_000;

  const numeric = (v: string): boolean => /^\d+$/.test(v.trim());
  // NOT `numeric`: tmux reports the signal by NAME, not number. Measured on
  // tmux 3.7b, `#{pane_dead_signal}` for a SIGTERM death is the string `term`
  // — so a numeric test never matches, and a signal death would sit out the
  // whole bound before reporting the unknown it already knew about. Any
  // non-empty value is the answer; builds without the format expand it to ''.
  //
  // The `#{` rejection is the fail-safe direction. A build that echoed the
  // format back unexpanded would otherwise make EVERY dead pane resolve as a
  // signal on the first read — skipping the status wait entirely and turning
  // this fix into a faster version of the bug it closes.
  const signalled = (v: string): boolean => v.trim().length > 0 && !v.includes('#{');
  const started = Date.now();
  const deadline = started + waitMs;

  let reading = initial;
  let pane = '';
  let polls = 0;
  let readFailures = 0;
  // ENG-10714 (CodeRabbit): declared HERE, not beside the retry below, because
  // the periodic banner capture inside the loop can fail too — and if it does
  // while the pane is still empty, a later final capture that succeeds-and-is-
  // empty would report zero failures and `describeClaudeExit` would go back to
  // claiming "It printed nothing". That is this change's own defect, one loop up.
  let captureFailures = 0;
  let nextCaptureAt = started + captureEveryMs;
  let resolved: DeadPaneVerdict['resolved'] = 'timeout';
  if (numeric(reading.exitStatus)) resolved = 'status';
  else if (signalled(reading.signal)) resolved = 'signal';

  while (resolved === 'timeout' && Date.now() < deadline) {
    await sleep(pollMs);
    polls++;
    try {
      reading = await io.read();
    } catch (err) {
      readFailures++;
      // A transient tmux client spawn failure under load must NOT collapse the
      // wait. The previous `catch { break }` here turned one such failure into
      // an immediate "exit status unknown", which is the same wrong answer
      // this whole wait exists to avoid — and it left no trace of having
      // happened. Only a session that has genuinely gone away ends the wait,
      // and even then the pane was already dead, so the verdict is still
      // `claude-exited` rather than a bare `no-session`.
      if (classifyTmuxError(err).kind !== 'unknown') { resolved = 'session-gone'; break; }
      continue;
    }
    if (numeric(reading.exitStatus)) { resolved = 'status'; break; }
    if (signalled(reading.signal)) { resolved = 'signal'; break; }
    if (Date.now() >= nextCaptureAt) {
      nextCaptureAt = Date.now() + captureEveryMs;
      try { pane = await io.capture(); } catch { captureFailures++; }
      // Costs one capture per second, and covers the tmux builds that publish
      // no `pane_dead_signal`: on those, a signal death would otherwise sit
      // here for the full bound before admitting it has no status.
      if (/^Pane is dead \(/m.test(pane)) { resolved = 'banner'; break; }
    }
  }

  // Capture last, so the pane read is the freshest one available — on `status`
  // the banner is 3–24 ms behind and may only now have landed. Attempted even
  // on `session-gone`: a `display-message` that classified as no-session does
  // not prove `capture-pane` fails in the same instant, and the pane content
  // is the last thing recoverable about why claude exited.
  //
  // ENG-10714: this was one `try { pane = await io.capture(); } catch {}`, and
  // that single line produced a quarter of the CI failures on this path — 5 of
  // 20 over the test file's first 38.7 h were an EMPTY pane, once on a pane
  // whose exit status had already been published. The operator was told
  // "It printed nothing" about a pane that had printed.
  //
  // Two candidate causes, and the swallowing is what made them
  // indistinguishable: tmux had not yet drained the pty into the pane buffer
  // (the pane is marked dead at EOF, which is earlier), or the capture's own
  // tmux client spawn failed transiently under load — the very class the read
  // loop above already retries and COUNTS. Retrying fixes both; counting is
  // what lets the next occurrence say which one it was.
  //
  // Bounded, and entered only when there is nothing to show: a pane with
  // content costs exactly one capture, exactly as before.
  // Snapshot BEFORE the capture retry: `waitedMs` is how long tmux was given
  // to publish a STATUS, and the operator message quotes it as exactly that.
  // Letting the capture retry inflate it would make the reported wait disagree
  // with the bound it is being compared against.
  const waitedMs = Date.now() - started;
  const captureDeadline = Date.now() + (opts.captureRetryMs ?? 1_000);
  const captureRetryPollMs = opts.captureRetryPollMs ?? 200;
  let recaptures = 0;
  for (;;) {
    try {
      const fresh = await io.capture();
      // Never trade content away for a later, emptier read: an in-loop banner
      // capture may already hold claude's output, and on `session-gone` the
      // pane can legitimately go blank between reads.
      //
      // Compared by CHARACTER length of the filtered output, not by line count
      // (CodeRabbit): two captures can both hold one line while the later one
      // is a truncated version of the earlier, and a line-count test would
      // swap the diagnostic for the fragment.
      const freshOutput = paneOutputLines(fresh).join('\n');
      const savedOutput = paneOutputLines(pane).join('\n');
      if (freshOutput.length >= savedOutput.length) pane = fresh;
    } catch (err) {
      captureFailures++;
      // Same rule as the read loop: only a session that has genuinely gone
      // away ends this, because retrying a vanished session cannot succeed.
      if (classifyTmuxError(err).kind !== 'unknown') break;
    }
    if (paneOutputLines(pane).length > 0 || Date.now() >= captureDeadline) break;
    recaptures++;
    await sleep(captureRetryPollMs);
  }
  return {
    exitStatus: reading.exitStatus,
    pane,
    resolved,
    waitedMs,
    polls,
    readFailures,
    captureFailures,
    recaptures,
  };
}

/**
 * ENG-10617: turn a dead pair pane into an operator-readable message.
 *
 * Pair sessions run with `remain-on-exit on`, so when claude exits the
 * pane stays behind holding its final screen plus tmux's own
 * `Pane is dead (status N, <date>)` banner. That screen is the only record
 * of why claude exited — without it the operator got a raw
 * `can't find pane` and nothing to act on.
 *
 * Exported for unit testing.
 */
export function describeClaudeExit(exitStatus: string, pane: string, wait?: DeadPaneVerdict): string {
  // ENG-10631: `pane_dead_status` can still be empty after the reap wait under
  // heavy load (seen in CI with a 1s wait), but tmux's own banner in the pane
  // carries the same number. Use it rather than report "exit status unknown".
  // The LAST banner: claude's own output above it could contain banner-shaped
  // text, and tmux always writes the real one after everything claude printed.
  // Take the final banner of ANY kind first (a signal death writes
  // `Pane is dead (signal N, ...)`), and only then read a status out of it.
  const finalBanner = [...pane.matchAll(/^Pane is dead \(.*$/gm)].at(-1)?.[0] ?? '';
  const bannerStatus = /^Pane is dead \(status (\d+)/.exec(finalBanner)?.[1] ?? '';
  if (!/^\d+$/.test(exitStatus.trim()) && bannerStatus) exitStatus = bannerStatus;
  const lines = paneOutputLines(pane);
  let output = lines.slice(-15).join('\n');
  if (output.length > 800) output = `…${output.slice(-800)}`;
  const known = /^\d+$/.test(exitStatus.trim());
  // ENG-10653: "we waited and tmux never said" and "tmux said there is no exit
  // status" are different facts, and only the first is a defect. A signal death
  // is a real status-less exit and keeps the plain wording; a wait that ran out
  // says so, and carries what it saw — otherwise the next occurrence of this
  // flake is as undiagnosable as the one that opened this issue.
  let unresolved = '';
  if (wait?.resolved === 'timeout') {
    unresolved = ` — tmux had not published one after ${wait.waitedMs}ms (${wait.polls} polls, ` +
      `${wait.readFailures} read failure(s), ` +
      `${finalBanner ? 'banner present' : 'no dead-pane banner'}` +
      `${wait.tmuxVersion ? `; ${wait.tmuxVersion}` : ''})`;
  } else if (wait?.resolved === 'session-gone') {
    // Also unanswered, just for a different reason, and it needs saying for
    // the same one: without it this reads as a plain status-less exit, which
    // is the bare-diff shape this whole change exists to remove.
    unresolved = ` — the tmux session disappeared after ${wait.waitedMs}ms ` +
      `(${wait.polls} polls) before tmux published one`;
  }
  const status = known ? `exit status ${exitStatus.trim()}` : `exit status unknown${unresolved}`;
  // ENG-10714: "It printed nothing" is a CLAIM about claude, and it must only
  // be made when the capture actually succeeded and came back empty — which,
  // after the bounded retry above, means several clean reads spread over a
  // second. If a capture FAILED we do not know what claude printed, and saying
  // it printed nothing is the same shape of confident falsehood ENG-10617
  // exists to remove: the operator stops looking for output that was there.
  const nothing = (wait?.captureFailures ?? 0) > 0
    ? `Its output could not be read back from the dead pane ` +
      `(${wait?.captureFailures} capture failure(s)), so it may have printed something. `
    : 'It printed nothing. ';
  return (
    `Claude Code exited on the host (${status}) before login finished. ` +
    (output ? `Its last output:\n${output}\n` : nothing) +
    'Run `claude` on the host as the manager\'s user to reproduce, then Start over.'
  );
}

/**
 * ENG-10617: null while claude is still running in the pair pane; a
 * `claude-exited` error (with its last output) once it has exited; or the
 * classified tmux error if the session itself is gone.
 */
async function checkPairPaneAlive(session: string, opts: DeadPaneWaitOpts = {}): Promise<SessionError | null> {
  const read = async (): Promise<DeadPaneReading> => {
    const { stdout } = await execFileAsync('tmux', [
      'display-message',
      '-p',
      '-t',
      session,
      // `|`-separated, not space-separated: an unset format expands to the
      // empty string, so splitting three fields on a space would shift them
      // left and read the signal as the status.
      '#{pane_dead}|#{pane_dead_status}|#{pane_dead_signal}',
    ]);
    const [dead = '', exitStatus = '', signal = ''] = stdout.trim().split('|');
    return { dead, exitStatus, signal };
  };
  let initial: DeadPaneReading;
  try {
    initial = await read();
  } catch (err) {
    return classifyTmuxError(err);
  }
  // Fast path: an alive pane costs exactly one tmux call and never captures.
  if (initial.dead !== '1') return null;
  const verdict = await awaitDeadPaneVerdict(
    { read, capture: () => capturePane(session, { scrollback: -50 }) },
    initial,
    opts,
  );
  if (verdict.resolved === 'timeout') {
    try {
      const { stdout } = await execFileAsync('tmux', ['-V']);
      verdict.tmuxVersion = stdout.trim();
    } catch { /* best-effort: the version is a diagnostic, not a requirement */ }
  }
  return { kind: 'claude-exited', message: describeClaudeExit(verdict.exitStatus, verdict.pane, verdict) };
}

// ---------------------------------------------------------------------------
// Pair-scoped tmux session lifecycle
// ---------------------------------------------------------------------------

/**
 * ENG-10653: the dead-pane banner, pinned rather than inherited.
 *
 * The banner text is an OPTION (`remain-on-exit-format`, tmux 3.3+), not a
 * constant, and a pair session is created on whatever tmux server the host
 * already runs — so it inherits the host user's `~/.tmux.conf`. A host that
 * sets its own format silently disables BOTH readers of that banner:
 * ENG-10631's status fallback and ENG-10653's completion marker. Verified:
 * with `remain-on-exit-format "child gone: #{pane_dead_status}"` the pane
 * holds `child gone: 3` and `/^Pane is dead \(/` matches nothing, while the
 * status sits in plain view. No error, no log — the shape this whole issue is
 * about, one level down.
 *
 * So the pair session states the format it intends to parse.
 *
 * The `#{!=:...,}` guard is not decoration, and tmux's own default carries it
 * for the same reason: `#{?pane_dead_status,...}` would test TRUTHINESS, and
 * tmux reads `0` as false — so a clean `exit 0` would render as a signal
 * death. Comparing against the empty string asks the question actually meant,
 * "did tmux publish a status".
 */
export const PAIR_REMAIN_ON_EXIT_FORMAT =
  'Pane is dead (#{?#{!=:#{pane_dead_status},},status #{pane_dead_status},signal #{pane_dead_signal}})';

/**
 * Spawn a throwaway tmux session running `claude` for the pairing flow.
 * Idempotent — if the session already exists, returns success silently.
 * Caller is responsible for `killPairSession` once the pair reaches a
 * terminal state.
 *
 * Uses an absolute path to the claude binary so we don't depend on the
 * inherited PATH (the manager runs with cloud-init's minimal env on
 * EC2). resolveClaudeBinary checks CLAUDE_PATH, then `which`, then
 * canonical Linux/macOS Homebrew install dirs.
 */
export async function spawnPairSession(session: string): Promise<{ ok: true } | { ok: false; error: SessionError }> {
  try {
    await execFileAsync('tmux', ['has-session', '-t', session]);
    return { ok: true };
  } catch {
    // session doesn't exist yet — fall through to create it
  }

  const { resolveClaudeBinary } = await import('./persistent-session.js');
  const claudeBin = resolveClaudeBinary();

  // ENG-5070: backfill HOME/USER before spawning the pair tmux.
  // persistent-session.ts already does this for agent sessions
  // (ENG-4632) but the pair flow re-used execFileAsync's inherited
  // env, so the same SSM-launched-manager-strips-HOME trap fired the
  // moment an operator clicked "Login via browser" on a fresh host:
  // tmux inherits HOME='', the new claude process can't read or
  // write ~/.claude/, claude exits within milliseconds, and the
  // pairing UI surfaces the generic "claude exited immediately"
  // message. Treat empty-string as missing too — HOME="" makes ~
  // resolve to cwd (whatever happens to be the manager's working
  // dir), which is the same broken outcome as no HOME, just better
  // hidden.
  const pairEnv: NodeJS.ProcessEnv = {
    ...process.env,
    HOME: (process.env.HOME?.trim()) || homedir(),
    USER: (process.env.USER?.trim()) || userInfo().username,
  };

  // ENG-10869 — the pair pane needs CLAUDE_CONFIG_DIR passed BY ARGV.
  //
  // `{ env: pairEnv }` below sets the environment of the tmux CLIENT, and a
  // tmux pane does not inherit that: it gets the SERVER's global environment,
  // merged with `-e` entries, plus only the names in `update-environment`
  // (ENG-8800 measured this on a live host — see the long note in
  // persistent-session.ts, which exists because a per-agent variable passed
  // this way leaked one agent's identity to every other agent on the box).
  //
  // The consequence here is specific and bad. Without the `-e`, `claude` in
  // this pane writes `<home>/.claude.json` while `finalizeClaudePairOnboarding`
  // polls `<configDir>/.claude.json` for an mtime that can never move. Every
  // pairing on every host then exhausts its dialog budget — which under
  // ENG-10862 is no longer a warning but a REPORTED FAILURE, an alert and an
  // instruction to start over, for pairings that actually worked. Meanwhile the
  // containers keep reading the pre-pair copy and the next spawn archives the
  // freshly-paired file.
  //
  // Only when the config dir already holds a usable config: on a host that has
  // never paired there is nothing there yet, and the bootstrap path is exactly
  // that claude writes the legacy file and the next spawn seeds from it.
  const pairConfigDir = claudeConfigDirFor();
  const pairConfigEnvArgs =
    inspectClaudeConfig(claudeJsonPathFor()) === 'usable'
      ? ['-e', `${CLAUDE_CONFIG_DIR_ENV}=${pairConfigDir}`]
      : [];

  try {
    // -x 240 -y 50 — wide enough that Claude Code's OAuth URL (~350
    // chars after PKCE expansion) doesn't soft-wrap. The dewrap pass in
    // extractOAuthUrl handles any leftover wrapping, but giving the URL
    // a single line in the first place is more robust against future
    // claude UI changes.
    // ENG-10617: `remain-on-exit on` is chained into the SAME tmux
    // invocation (`;` is tmux's command separator) so it is applied before
    // the server can reap an early-exiting claude. Without it, claude
    // exiting destroys the session and its last output with it, and every
    // later tmux call fails with a bare `can't find pane`.
    await execFileAsync(
      'tmux',
      [
        'new-session',
        '-d',
        '-x',
        '240',
        '-y',
        '50',
        ...pairConfigEnvArgs,
        '-s',
        session,
        claudeBin,
        ';',
        'set-option',
        '-w',
        '-t',
        session,
        'remain-on-exit',
        'on',
      ],
      { env: pairEnv },
    );
  } catch (err) {
    return { ok: false, error: classifyTmuxError(err) };
  }

  // ENG-10653: pin the banner, BEST-EFFORT AND DELIBERATELY NOT CHAINED.
  //
  // `remain-on-exit-format` is tmux 3.3+, and tmux exits 1 on an unknown
  // option while still creating the session — measured. Chained into the
  // new-session above, that would turn "this host runs tmux 3.2" into a failed
  // pair AND an orphaned session, trading a silent degradation for an outage.
  // Separate and swallowed, an old tmux simply keeps the host's banner, which
  // is exactly the behaviour it had before this change.
  //
  // The window this opens — claude dying between the two calls — resolves to
  // that same pre-existing behaviour, not to anything worse.
  try {
    await execFileAsync(
      'tmux',
      ['set-option', '-w', '-t', session, 'remain-on-exit-format', PAIR_REMAIN_ON_EXIT_FORMAT],
      { env: pairEnv },
    );
  } catch { /* tmux < 3.3, or the option is gone: fall back to the host's banner */ }

  // `tmux new-session` returns 0 even if the command exited immediately
  // (binary missing, claude crashed, etc.) — the session is created and
  // then torn down. Without this verification we'd propagate a generic
  // `session_missing` to the operator on the next has-session call,
  // hiding the real failure. Sleep briefly to give claude a beat to
  // crash-or-stay-alive, then check.
  await sleep(500);
  const dead = await checkPairPaneAlive(session);
  if (dead?.kind === 'claude-exited') return { ok: false, error: dead };
  if (dead) {
    return {
      ok: false,
      error: {
        kind: 'unknown',
        message: `claude exited immediately after launch (binary at ${claudeBin}). Run \`${claudeBin}\` manually on the host to see why — likely missing TTY, missing HOME, or a startup error.`,
      },
    };
  }
  return { ok: true };
}

/**
 * Has a USABLE config landed at `path` since `sinceMtimeMs`?
 *
 * Both halves matter: the mtime advance distinguishes this pairing's write from
 * a file left by a previous attempt, and the parse distinguishes a write from a
 * tear (ENG-10140 truncations are a plausible size).
 *
 * Exported because finalize is not the only thing that has to ask. The caller
 * re-asks before it reports a failure — see {@link confirmPairFailure} — and a
 * second hand-written copy of this rule is how the two would come to disagree
 * about what "landed" means.
 */
export function pairConfigLandedSince(path: string, sinceMtimeMs: number): boolean {
  try {
    if (!existsSync(path)) return false;
    if (statSync(path).mtimeMs <= sinceMtimeMs) return false;
    // USABLE, not merely parseable.
    //
    // This was `JSON.parse(...)` and that is a false success. Measured on
    // dominet 2026-09-22T21:26Z, on a host running the ENG-10862 fix: claude
    // wrote a 45KB config carrying a real `oauthAccount` for dom@dominet.com,
    // `hasCompletedOnboarding` ABSENT. It parsed, so finalize reported
    // "onboarding persisted" and all five agents stayed on the login picker.
    // That is the exact condition ENG-10862 exists to end, surviving its own
    // fix because the fix corrected WHEN the check runs and not WHAT it tests.
    //
    // It is not only a mis-report, it is a CAUSE. The log read
    // `iterations=1 dialogEnter=0 consumerTerms=0`: finalize saw the first
    // write, declared success, and tore down the pane having dismissed ZERO
    // dialogs — leaving claude sitting on the onboarding prompt that would have
    // written the flag. Requiring the flag keeps the loop pressing Enter until
    // onboarding actually completes, which is what the loop is for.
    //
    // `inspectClaudeConfig` is the same rule the config-dir readiness and mount
    // gates use. Two definitions of "a good config" in one codebase is how this
    // got through; there is now one.
    return inspectClaudeConfig(path) === 'usable';
  } catch {
    return false;
  }
}

/**
 * ENG-10862 — give a late write one last chance before calling a pairing failed.
 *
 * `finalizeClaudePairOnboarding` can return `send-failed` or `pane-gone`
 * IMMEDIATELY after its last config check: those branches abort on a tmux error
 * rather than falling through to the grace pass, so unlike `not-flushed` they
 * have observed the file exactly once, milliseconds earlier. A write already in
 * flight then lands after the verdict.
 *
 * That matters more than it used to. Before this issue the verdict was a log
 * line; now it gates the report, and `reportAndCleanup` POSTs the result BEFORE
 * its three-second beat — so a pairing that actually worked would raise a
 * `warning` alert and tell the operator to start over.
 *
 * So the caller waits and re-asks. The wait is NOT the same as the beat in
 * `reportAndCleanup`: that one exists to let Claude Code finish persisting
 * `.credentials.json` before the pane is killed, and it happens after the POST.
 * This one exists to make the POST correct.
 */
export async function confirmPairFailure(opts: {
  configPath: string;
  sinceMtimeMs: number;
  graceMs?: number;
  sleep?: (ms: number) => Promise<void>;
  log?: (msg: string) => void;
}): Promise<'landed-after-all' | 'confirmed'> {
  const graceMs = opts.graceMs ?? 3_000;
  const nap = opts.sleep ?? sleep;
  const log = opts.log ?? (() => {});
  if (pairConfigLandedSince(opts.configPath, opts.sinceMtimeMs)) {
    log(`[claude-pair] a usable config was already present at ${opts.configPath} — not a failure`);
    return 'landed-after-all';
  }
  await nap(graceMs);
  if (pairConfigLandedSince(opts.configPath, opts.sinceMtimeMs)) {
    log(
      `[claude-pair] ${opts.configPath} landed during the ${graceMs}ms confirm window — ` +
        `the pairing worked after all, reporting success`,
    );
    return 'landed-after-all';
  }
  return 'confirmed';
}

/**
 * Why finalize concluded that onboarding did not persist. ENG-10862.
 *
 * One WARN covered all of these before, which is what made the alyve incident
 * unreadable: the message asserted a timeout ("reached 10 iterations") for a
 * host whose config was simply unwritable. Only `not-flushed` is a timeout.
 */
export type PairFinalizeFailure =
  /** The watched path holds a file that does not parse — the ENG-10140 fault. */
  | 'config-corrupt'
  /** Nothing at the watched path at all. Claude Code never wrote one. */
  | 'config-absent'
  /** A parseable config is there, but its mtime never advanced in the window. */
  | 'not-flushed'
  /**
   * A DIRECTORY sits where the config file belongs.
   *
   * Not hypothetical, and not a corruption: `docker run -v /nonexistent:/path`
   * creates a directory at the target, which is how `/root/.claude.json` became
   * one on isolated hosts. `readFileSync` gives EISDIR, so without its own
   * reason this reported `config-corrupt` and told the operator to re-pair —
   * which cannot fix it, so they would loop.
   */
  | 'config-path-is-directory'
  /** The pane died, so nothing was left that could write the file. */
  | 'pane-gone'
  /** tmux refused a keystroke we needed to send to clear a dialog. */
  | 'send-failed';

/**
 * ENG-10862 AC5: how the dialog loop actually spent its iterations.
 *
 * The question "do real pairings need more than 10 dismissals?" was
 * unanswerable because nothing recorded the breakdown. Counts only — never the
 * pane text, which carries `Logged in as <email>`.
 */
export interface PairDialogCounts {
  /** ENG-10619 consumer-terms dialog answered (option 2, training OFF). */
  consumerTerms: number;
  /** A generic post-OAuth dialog dismissed with Enter. */
  dialogEnter: number;
  /** Pane matched no known dialog — iterations spent purely waiting. */
  noMatch: number;
}

export interface PairFinalizeResult {
  finalized: boolean;
  /**
   * Dialog-loop iterations only, capped at `maxIterations`. Deliberately NOT
   * inclusive of the grace pass below, so the number keeps the meaning callers
   * and tests already depend on.
   */
  iterations: number;
  /** Grace-pass waits consumed (ENG-10862). Zero unless the dialog loop ran out. */
  graceWaits: number;
  counts: PairDialogCounts;
  /** Set if and only if `finalized` is false. */
  reason?: PairFinalizeFailure;
  /**
   * The config path this call actually watched. Returned so the caller can
   * name it in the operator-facing message without recomputing the rule — the
   * two would drift, and the symptom would be an error message about a file
   * the flow never looked at.
   */
  configPath: string;
}

/** Stable machine-readable code per failure, for `claude_pair_sessions.error_code`. */
export const PAIR_FINALIZE_ERROR_CODES: Record<PairFinalizeFailure, string> = {
  'config-corrupt': 'onboarding_config_corrupt',
  'config-absent': 'onboarding_config_absent',
  'config-path-is-directory': 'onboarding_config_path_is_directory',
  'not-flushed': 'onboarding_not_flushed',
  'pane-gone': 'onboarding_pane_gone',
  'send-failed': 'onboarding_send_failed',
};

/**
 * Operator-facing text for a finalize failure.
 *
 * **Every message says the OAuth tokens are valid**, because the pairing is now
 * reported as a `failure` and the console renders this string in a danger box.
 * Without that sentence the operator reads "failed" and goes hunting a token
 * fault that does not exist — the tokens were written to
 * `~/.claude/.credentials.json` before finalize ever ran. What failed is the
 * onboarding state that stops the next agent spawn landing on the login picker.
 */
export function describePairFinalizeFailure(
  reason: PairFinalizeFailure,
  configPath: string,
): string {
  const valid =
    'The OAuth tokens themselves are valid, so this is not a credential problem.';
  const retry = 'Start over to re-run pairing.';
  switch (reason) {
    case 'config-corrupt':
      // Deliberately carries NO ticket id: this string renders in the console,
      // which customers use to pair their own hosts, and they cannot read
      // Linear. The ENG-10140 pointer is in the host's log line instead, where
      // the audience is us.
      return `Signed in, but ${configPath} on this host is unreadable and Claude Code did not replace it, so agents here would start on the login picker. ${valid} ${retry} If it recurs on this host, contact support — the host's config is being corrupted by something outside pairing.`;
    case 'config-path-is-directory':
      // The remedy is genuinely different, so the message has to be too:
      // re-pairing cannot help until the directory is gone.
      return `Signed in, but ${configPath} on this host is a DIRECTORY, not a file, so Claude Code cannot write its config there and agents here would start on the login picker. ${valid} Re-pairing will NOT fix this on its own: remove that directory on the host, then pair again.`;
    case 'config-absent':
      return `Signed in, but Claude Code never wrote ${configPath}, so agents on this host would start on the login picker. ${valid} ${retry}`;
    case 'not-flushed':
      // Covers BOTH "never written" and the dominet case: written, parseable,
      // carrying a real account, with `hasCompletedOnboarding` absent. The old
      // wording claimed the file was not written, which on that host was false
      // and sent the operator looking for a disk problem.
      return `Signed in, but Claude Code did not finish onboarding on this host, so ${configPath} is not usable yet and agents here would start on the login picker. ${valid} ${retry}`;
    case 'pane-gone':
      return `Signed in, but the temporary Claude Code session on the host closed before it wrote ${configPath}, so agents here would start on the login picker. ${valid} ${retry}`;
    case 'send-failed':
      return `Signed in, but tmux refused a keystroke needed to clear Claude Code's post-login dialogs, so ${configPath} was never written and agents here would start on the login picker. ${valid} ${retry}`;
  }
}

/**
 * The `.claude.json` the pair flow watches.
 *
 * Exported so a caller can snapshot the SAME path finalize will watch. Two
 * independent spellings of this rule is how the baseline below would silently
 * stop applying.
 */
/**
 * The path finalize watches, reconciling ENG-10862 and ENG-10869.
 *
 * An explicitly-set CLAUDE_CONFIG_DIR wins because the pair process inherits
 * it and WILL write there — including before that directory holds a usable
 * config, which is precisely the window `effectiveClaudeJsonPath()` still
 * resolves to the legacy path. Everywhere else, the shared resolution, so
 * finalize and the pane's environment cannot disagree about which file this
 * pairing is writing.
 */
export function finalizeWatchPath(env: NodeJS.ProcessEnv = process.env): string {
  if (env['CLAUDE_CONFIG_DIR']?.trim()) return pairClaudeJsonPath(env);
  return effectiveClaudeJsonPath();
}

export function pairClaudeJsonPath(env: NodeJS.ProcessEnv = process.env): string {
  // CLAUDE_CONFIG_DIR FIRST, because that is what the pair process itself
  // obeys. `spawnPairSession` builds the pair env as `{...process.env, HOME}`,
  // so a CLAUDE_CONFIG_DIR in the manager's environment is inherited and
  // Claude Code writes `$CLAUDE_CONFIG_DIR/.claude.json`. Watching
  // `~/.claude.json` unconditionally would watch a file nothing writes.
  //
  // Before ENG-10862 that mismatch cost a WARN and a `success` report. It is
  // strictly worse now: `flushed()` never fires, the give-up classifier says
  // `config-absent`, the re-ask confirms against the same wrong path, and the
  // pairing reports `failure` — a page, plus a console box telling the
  // operator to start over, which re-runs the identical mismatch. Every
  // pairing on such a host would fail forever with no way out from the
  // console. Precedent for the precedence: `claudeConfigCandidatePaths` in
  // `claude-account-fingerprint.ts` puts CLAUDE_CONFIG_DIR first for the same
  // reason.
  const configDir = env['CLAUDE_CONFIG_DIR']?.trim();
  if (configDir) return join(configDir, '.claude.json');
  return join(homedir(), '.claude.json');
}

/** A path plus the mtime it had at some earlier instant. */
export interface PairConfigBaseline {
  path: string;
  mtimeMs: number;
}

/**
 * ENG-10862 — snapshot the config's mtime BEFORE the auth code is submitted.
 *
 * ## Why the caller has to take this, and finalize cannot
 *
 * finalize runs only after `submitClaudePairCode` has seen the success marker
 * in the pane, so a snapshot taken at its entry is already too late: Claude
 * Code may have written the config between the marker appearing and finalize
 * starting, and those are concurrent events, not ordered ones. The mtime has
 * then ALREADY advanced, finalize compares against the post-write value, sees
 * no further change, and reports a failure for a pairing that worked.
 *
 * Under the old code that produced a WARN and a `success` report, so the
 * mistake was invisible. Now that the report is gated on this verdict it would
 * be a page, a red console and an instruction to start over, against a host
 * that is fine — a strictly worse failure than the one being fixed, and
 * introduced by fixing it.
 */
export function snapshotPairConfig(claudeJsonPath = finalizeWatchPath()): PairConfigBaseline {
  return {
    path: claudeJsonPath,
    mtimeMs: existsSync(claudeJsonPath) ? statSync(claudeJsonPath).mtimeMs : 0,
  };
}

/**
 * ENG-4633: drive Claude Code through its post-OAuth dialogs so it
 * persists `~/.claude.json` (the device-state + onboarding-complete
 * file) before we tear down the pair tmux session.
 *
 * Without this step, the pair flow leaves the host with only
 * `~/.claude/.credentials.json` written. On the next agent launch,
 * Claude Code interactive mode sees no `~/.claude.json` and falls
 * back to the login picker — even though the OAuth tokens are
 * sitting right there. This was the root cause of the 2026-05-01
 * prod scout outage.
 *
 * After successful code submission, claude shows in sequence:
 *   1. "Logged in as <email> / Login successful. Press Enter to continue…"
 *   2. "Security notes / Press Enter to continue…"
 *   3. (depending on cwd) trust-folder prompt and/or bypass-permissions
 *      warning. The pair tmux session has neither a project dir nor
 *      `--dangerously-skip-permissions`, so we usually only see the
 *      first two.
 *
 * ## ENG-10862: the success check has to come FIRST
 *
 * This loop used to stat the config at the BOTTOM, after the dialog matchers —
 * and both matcher branches `continue`. The matchers are deliberately loose
 * (claude's wording shifts between versions) and `Logged in as <email>` stays
 * visible in the pane after login, so on any run where that text persisted the
 * loop sent Enter ten times and **never looked at the file at all**. Exhausting
 * the cap was then guaranteed whether or not Claude Code had written the
 * config, and the give-up message blamed a timeout either way.
 *
 * That is the alyve-host-1 failure of 2026-09-21: one pairing in three gave up
 * at the cap, four agents latched on the login picker for up to 9h50m, and the
 * only signal was a WARN nobody read for nine hours. The runs that succeed log
 * `after 3 dialog dismissal(s)`, which establishes that the pane *usually*
 * clears by the third iteration — not that it must.
 *
 * So the file is checked at the top of every iteration, before any matcher runs
 * and before the pane-liveness probe: a config that landed is a success even if
 * the pane has since died, and no amount of residual dialog text can mask it.
 *
 * ## Success now requires the file to PARSE, not merely to change
 *
 * mtime-only was the old contract, on the stated grounds that we trust claude
 * to write a coherent file. ENG-10140 falsified that: on a containerised host
 * the shared config is routinely torn by concurrent truncate-in-place writes,
 * and an mtime that advances onto a corrupt file is not onboarding having
 * succeeded — it is the next spawn's login picker with a fresher timestamp. A
 * transient mid-write read is covered by simply continuing to poll.
 *
 * ## The grace pass, and where its budget comes from
 *
 * `maxIterations` reads like a dialog count and is really a timer: 10 × 1500ms
 * is a **15-second** total budget. When it runs out, this makes one longer,
 * quieter pass before returning, because the caller's next act is to kill the
 * pane and the pane is the only thing left that can still write the file. The
 * grace pass sends no blind Enter — ten have already gone — but it does keep
 * answering the consumer-terms dialog, since a bare Enter on that one opts the
 * account into model training (ENG-10619) and leaving it unanswered is itself a
 * way to never get the write.
 *
 * **10 more intervals, i.e. 30s total.** An earlier version of this comment
 * derived that number from a heartbeat constraint, and the constraint does not
 * exist: it argued that because `processClaudePairSessions` is awaited inside
 * the refresh cycle, a longer finalize starves `last_seen_at` and a failing
 * pairing could make its own host look stale. ENG-7273 decoupled those —
 * `livenessTimer` is an independent 30s `setInterval` that POSTs
 * `/host/heartbeat` regardless of how long a cycle takes, and finalize only
 * ever awaits `sleep()` and `execFileAsync`, so the interval keeps firing
 * throughout. Reported by a reviewer, and confirmed against
 * `LIVENESS_HEARTBEAT_INTERVAL_MS` before this was rewritten.
 *
 * So the budget is a JUDGEMENT, not a derivation, and it should be read as one:
 * 30s is long enough to catch a write that the 15s dialog loop missed, short
 * enough that an operator watching the pairing UI is not left on a spinner, and
 * it delays only a pairing that is already failing. The real constraint is the
 * console's patience, and nobody has measured that. Anyone extending it is not
 * fighting a heartbeat bound — that reason was wrong.
 *
 * Nothing upstream races the longer window — checked rather than assumed. The
 * console polls `code_submitted` with a spinner and no deadline, and no cron
 * sweeps a stale pair row, so the only cost of waiting is the spinner running
 * longer on a pairing that was going to fail anyway.
 *
 * Returns `finalized` plus a `reason` the caller reports as a FAILED pairing.
 * The OAuth tokens are valid regardless, which every operator-facing message
 * says explicitly — see {@link describePairFinalizeFailure}.
 */
export async function finalizeClaudePairOnboarding(
  session: string,
  log: (msg: string) => void,
  opts: {
    maxIterations?: number;
    intervalMs?: number;
    claudeJsonPath?: string;
    graceIterations?: number;
    /**
     * ENG-10862: the mtime to measure against, taken by the caller BEFORE the
     * auth code was submitted. See {@link snapshotPairConfig} for why a
     * snapshot taken at entry here is already too late.
     */
    baseline?: PairConfigBaseline;
  } = {},
): Promise<PairFinalizeResult> {
  const maxIterations = opts.maxIterations ?? 10;
  const graceIterations = opts.graceIterations ?? 10;
  const intervalMs = opts.intervalMs ?? 1500;
  // ENG-10869 x ENG-10862: which file finalize watches. BOTH sides of this
  // merge were needed; neither alone is correct.
  //
  // finalize polls ONE path's mtime to decide whether onboarding took, so it
  // has to watch the file `claude` is actually writing.
  //
  //  - ENG-10869 resolves the config dir only once it holds a USABLE config,
  //    falling back to the legacy file. That keeps finalize in step with the
  //    pane's environment, which is gated on the same condition.
  //  - ENG-10862 honours an explicitly-set CLAUDE_CONFIG_DIR, because
  //    `spawnPairSession` builds the pair env as `{...process.env, HOME}` and
  //    the pair process therefore INHERITS it.
  //
  // They disagree in exactly one window, and it is the window that matters:
  // CLAUDE_CONFIG_DIR set while the config dir does not YET hold a usable
  // config — the first pairing on a migrated host. There, `claude` writes into
  // the config dir while the effective resolution still points at the legacy
  // file, so finalize would watch a file nothing writes. Under ENG-10862 that
  // is not a WARN any more: it is a reported FAILURE, an alert, and a console
  // box telling the operator to start over, which re-runs the identical
  // mismatch. Every pairing on such a host, forever.
  //
  // So: an explicit CLAUDE_CONFIG_DIR wins outright, and the shared resolution
  // answers everywhere else.
  //
  // `opts.baseline?.path` is deliberately NOT in this chain, though the first
  // draft of this merge put it there. Deriving the watch path FROM the baseline
  // does two bad things at once. It silently adopts whatever file the caller
  // happened to snapshot — and `snapshotPairConfig()` defaulted to
  // `pairClaudeJsonPath()`, so on a migrated host whose MANAGER env carries no
  // CLAUDE_CONFIG_DIR it returns the legacy file while ENG-10869 hands the pane
  // `-e CLAUDE_CONFIG_DIR=<dir>` and `claude` writes the config dir: finalize
  // watching a file nothing writes, which is the exact failure both these
  // tickets exist to remove. And it makes the mismatch guard below unfalsifiable,
  // since a path derived from the baseline can never disagree with it.
  //
  // Resolve independently; let the guard compare. The caller's snapshot is
  // still what the mtime is measured against — that is ENG-10862's late-write
  // fix and it is untouched — but only once the two agree about the file.
  const claudeJsonPath = opts.claudeJsonPath ?? finalizeWatchPath();

  // The mtime we measure against. Existence alone will not do: a stale file
  // from a previous pairing would satisfy that on the first iteration.
  //
  // The caller's baseline wins when it describes the same path, because it was
  // taken before the code was submitted and this one is taken after the login
  // already happened. A baseline for a DIFFERENT path is refused rather than
  // quietly ignored — ignoring it would reinstate exactly the too-late
  // snapshot it exists to prevent, with nothing to show for it.
  let initialMtime: number;
  if (opts.baseline && opts.baseline.path === claudeJsonPath) {
    initialMtime = opts.baseline.mtimeMs;
  } else {
    if (opts.baseline) {
      log(
        `[claude-pair] finalize: IGNORING a baseline for ${opts.baseline.path} — this run watches ` +
          `${claudeJsonPath}. A write that landed before the code was submitted may now read as a failure.`,
      );
    }
    initialMtime = existsSync(claudeJsonPath) ? statSync(claudeJsonPath).mtimeMs : 0;
  }

  const counts: PairDialogCounts = { consumerTerms: 0, dialogEnter: 0, noMatch: 0 };

  /**
   * Has claude written a USABLE config since we entered? Both halves matter:
   * the mtime advance distinguishes this pairing's write from a stale file left
   * by a previous attempt, and the parse distinguishes a write from a tear.
   */
  // Delegated, not reimplemented: the caller re-asks the same question before it
  // reports a failure, and two copies of "did a usable config land" would drift.
  // A parse failure here is either a mid-write or a genuine tear; keep polling,
  // and let the give-up classification below re-read and say which.
  const flushed = (): boolean => pairConfigLandedSince(claudeJsonPath, initialMtime);

  /** Fresh observation of the watched path, made at give-up time. */
  const classifyAtGiveUp = (): PairFinalizeFailure => {
    if (!existsSync(claudeJsonPath)) return 'config-absent';
    try {
      // Checked BEFORE the read, because a directory throws EISDIR from
      // `readFileSync` and would otherwise be reported as corruption — with a
      // remedy (re-pair) that cannot clear it.
      if (statSync(claudeJsonPath).isDirectory()) return 'config-path-is-directory';
      JSON.parse(readFileSync(claudeJsonPath, 'utf-8'));
      return 'not-flushed';
    } catch {
      return 'config-corrupt';
    }
  };

  const describeCounts = () =>
    `consumerTerms=${counts.consumerTerms} dialogEnter=${counts.dialogEnter} noMatch=${counts.noMatch}`;

  for (let i = 0; i < maxIterations; i++) {
    await sleep(intervalMs);

    // ENG-10862: FIRST, before the matchers and before the liveness probe. A
    // config that landed is a success even if the pane has since exited, and
    // residual dialog text must not be able to skip this.
    if (flushed()) {
      log(
        `[claude-pair] finalize: ${claudeJsonPath} updated (after ${i + 1} dialog dismissal(s); ${describeCounts()})`,
      );
      return { finalized: true, iterations: i + 1, graceWaits: 0, counts, configPath: claudeJsonPath };
    }

    const dead = await checkPairPaneAlive(session);
    if (dead) {
      log(`[claude-pair] finalize: pair session no longer running (${dead.kind}); aborting onboarding flow`);
      return { finalized: false, iterations: i, graceWaits: 0, counts, reason: 'pane-gone', configPath: claudeJsonPath };
    }
    let pane: string;
    try {
      pane = await capturePane(session);
    } catch (err) {
      const classified = classifyTmuxError(err);
      log(`[claude-pair] finalize: capture-pane failed (${classified.kind}); aborting onboarding flow`);
      return { finalized: false, iterations: i, graceWaits: 0, counts, reason: 'pane-gone', configPath: claudeJsonPath };
    }

    // ENG-10619: must run before the generic Enter branch below — a bare
    // Enter on this dialog confirms its default row, which opts the
    // account into model training.
    try {
      if (await answerConsumerTermsDialog(session, pane)) {
        counts.consumerTerms += 1;
        continue;
      }
    } catch {
      log('[claude-pair] finalize: send-keys failed on consumer-terms dialog; aborting onboarding flow');
      return { finalized: false, iterations: i, graceWaits: 0, counts, reason: 'send-failed', configPath: claudeJsonPath };
    }

    // Pattern-match the post-OAuth dialogs claude shows in succession.
    // Keep these matchers loose — claude's wording shifts between
    // versions, and a missed match just means we wait out this iteration
    // and re-check the file at the top of the next one.
    if (
      pane.includes('Login successful') ||
      pane.includes('Logged in as') ||
      /Press Enter to continue/i.test(pane) ||
      pane.includes('Security notes')
    ) {
      try {
        await sendKeys(session, 'C-m');
      } catch {
        // sendKeys failure → session dead. Same fallthrough as above.
        log('[claude-pair] finalize: send-keys failed; aborting onboarding flow');
        return { finalized: false, iterations: i, graceWaits: 0, counts, reason: 'send-failed', configPath: claudeJsonPath };
      }
      counts.dialogEnter += 1;
      continue;
    }

    counts.noMatch += 1;
  }

  // ENG-10862 AC4: one longer, quieter pass before the caller kills the pane.
  // No blind Enter here (ten have gone already); consumer-terms is still
  // answered because leaving it up is itself a reason the write never comes.
  log(
    `[claude-pair] finalize: ${maxIterations} dialog iterations elapsed (${describeCounts()}); waiting up to ${graceIterations} more intervals for the config`,
  );
  let graceWaits = 0;
  /**
   * Why the grace pass stopped early, if it did.
   *
   * It outranks the OBSERVED state for everything except a config that names a
   * specific fault. "The session died before writing" tells the operator
   * something that neither "no config" nor "not written in time" does.
   *
   * The first version let it win only over `config-absent`, which threw it away
   * on every host that has ever been paired: those have a parseable
   * `~/.claude.json`, so `classifyAtGiveUp()` returns `not-flushed` and a pane
   * that DIED during the grace pass was reported as a timeout — with the
   * message "Claude Code did not write … within the onboarding window" for a
   * session that had exited, and the wrong kind in the alert payload. The main
   * loop reports `pane-gone` for the identical event, so the two paths
   * disagreed. That is the timeout-for-everything conflation this type exists
   * to remove; `PairFinalizeFailure`'s own docblock says only `not-flushed` is
   * a timeout.
   *
   * A CORRUPT config, or a DIRECTORY at the path, still wins: those name an
   * actionable fault the pane's fate does not explain, and each carries its own
   * remedy.
   */
  let graceAborted: PairFinalizeFailure | null = null;
  for (let g = 0; g < graceIterations; g++) {
    await sleep(intervalMs);
    graceWaits = g + 1;
    if (flushed()) {
      log(
        `[claude-pair] finalize: ${claudeJsonPath} updated during grace pass (wait ${graceWaits}; ${describeCounts()})`,
      );
      return { finalized: true, iterations: maxIterations, graceWaits, counts, configPath: claudeJsonPath };
    }
    const dead = await checkPairPaneAlive(session);
    if (dead) {
      // Nothing is left that could write the file; waiting longer is theatre.
      log(`[claude-pair] finalize: pair session ended during grace pass (${dead.kind})`);
      graceAborted = 'pane-gone';
      break;
    }
    let pane: string;
    try {
      pane = await capturePane(session);
    } catch {
      graceAborted = 'pane-gone';
      break;
    }
    try {
      if (await answerConsumerTermsDialog(session, pane)) counts.consumerTerms += 1;
    } catch {
      graceAborted = 'send-failed';
      break;
    }
  }

  const observed = classifyAtGiveUp();
  // A config that names its own fault wins; otherwise an abort beats the
  // observed state, including `not-flushed`.
  const selfExplaining = observed === 'config-corrupt' || observed === 'config-path-is-directory';
  const reason = !selfExplaining && graceAborted ? graceAborted : observed;
  // The ticket pointers live HERE, not in the operator-facing message: this
  // line goes to manager.log, whose audience is us.
  const hint =
    reason === 'config-corrupt'
      ? ' — see ENG-10140 (shared-config corruption on containerised hosts)'
      : reason === 'config-path-is-directory'
        ? ' — a docker bind created a directory at this path; see ENG-10869'
        : '';
  log(
    `[claude-pair] finalize: giving up after ${maxIterations} dialog iterations + ${graceWaits} grace wait(s) — ` +
      `reason=${reason} (${describeCounts()}); ${claudeJsonPath} was not updated${hint}`,
  );
  return { finalized: false, iterations: maxIterations, graceWaits, counts, reason, configPath: claudeJsonPath };
}

/**
 * Returns true if the session is gone afterwards (either kill succeeded
 * or it was already missing). Returns false only if `kill-session`
 * failed for a non-missing-session reason — caller should treat this
 * as "still tracked, will retry next poll".
 */
export async function killPairSession(session: string): Promise<boolean> {
  try {
    await execFileAsync('tmux', ['kill-session', '-t', session]);
    return true;
  } catch (err) {
    // "can't find session" is success-equivalent — the desired end state
    // is "this session does not exist" and that's already true.
    if (classifyTmuxError(err).kind === 'no-session') return true;
    return false;
  }
}

// ---------------------------------------------------------------------------
// Result shapes
// ---------------------------------------------------------------------------

export type ClaudePairStartResult =
  | { kind: 'url'; url: string }
  | { kind: 'timeout' }
  | { kind: 'error'; error: SessionError };

export type ClaudePairSubmitResult =
  | { kind: 'success'; rawMatch: string }
  | { kind: 'failure'; rawMatch: string }
  | { kind: 'timeout' }
  | { kind: 'error'; error: SessionError };

export type ClaudePairStatusResult =
  | { kind: 'idle' }
  | { kind: 'awaiting-code'; url: string }
  | { kind: 'success' }
  | { kind: 'failure'; rawMatch: string }
  | { kind: 'session-missing' }
  | { kind: 'error'; error: SessionError };

/**
 * Detect Claude Code's workspace-trust dialog in a captured pane.
 *
 * ENG-5375: Claude Code 2.1.146 reworded the prompt from "Do you trust
 * the files in this folder?" to "Quick safety check: Is this a project
 * you created or one you trust?". Match on the option text instead of
 * the question — same shape as `acceptDialogs` in persistent-session.ts,
 * stable across Anthropic's question rewordings.
 *
 * Exported for unit testing.
 */
export function isTrustDialogVisible(pane: string): boolean {
  return /Yes,\s+I\s+trust\s+this\s+folder/i.test(pane);
}

/**
 * Detect Claude Code's regular interactive prompt in a captured pane.
 *
 * ENG-5377: Claude Code 2.1.146 replaced the older boxed `│ > ` cursor
 * with `❯ ` (U+276F) and the tip text is variable ("Try \"fix lint
 * errors\"" today — no " to "). Detect on the stable 2.1.146 markers:
 * the `❯` cursor and/or the `? for shortcuts` footer. Keep the legacy
 * shapes for back-compat with hosts that haven't picked up the newer
 * Claude Code binary yet.
 *
 * IMPORTANT: callers must check the dialog detectors (theme, trust,
 * login picker) BEFORE this, since `❯` also appears on those screens
 * as the selected-option indicator.
 *
 * Exported for unit testing.
 */
export function isInteractivePromptReady(pane: string): boolean {
  return (
    /❯/.test(pane) ||
    /\?\s+for\s+shortcuts/i.test(pane) ||
    /[│|]\s*>\s/.test(pane) ||
    /^\s*>\s*$/m.test(pane)
  );
}

// ---------------------------------------------------------------------------
// start — send `/login`, wait for the OAuth URL prompt
// ---------------------------------------------------------------------------

export interface ClaudePairStartOpts {
  /** Pair-scoped tmux session name (see pairTmuxSession). */
  session: string;
  /** Total time to wait for Claude Code to print the URL prompt. Default 60s. */
  timeoutMs?: number;
  /** How often to re-capture and check the pane. Default 500ms. */
  pollIntervalMs?: number;
}

export async function startClaudePair(opts: ClaudePairStartOpts): Promise<ClaudePairStartResult> {
  const { session } = opts;
  // Pair sessions cold-start `claude` from scratch — first-run on a fresh
  // host can take 10-30s before the prompt is interactive enough to
  // accept `/login`. Stay generous.
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const pollIntervalMs = opts.pollIntervalMs ?? 500;

  // Quick precheck — fail fast if the tmux session doesn't exist
  // rather than blasting `/login` into an unrelated pane.
  try {
    await execFileAsync('tmux', ['has-session', '-t', session]);
  } catch (err) {
    return { kind: 'error', error: classifyTmuxError(err) };
  }

  // Drive claude through its first-run onboarding to reach a state
  // where the OAuth URL is on screen. Possible paths:
  //
  //  • Fresh host: theme picker → "Trust folder?" → login method picker
  //    (option 1 = Claude subscription, already highlighted) → OAuth URL.
  //    No `/login` needed — option 1 IS the login.
  //  • Already onboarded: regular prompt → we send `/login` ourselves →
  //    login method picker → OAuth URL.
  //
  // Auto-advance every onboarding screen we recognize. Keep going until
  // we either see the URL prompt or hit the deadline.
  const onboardingDeadline = Date.now() + Math.min(45_000, timeoutMs);
  let lastDispatchAt = 0;
  let loginCommandSent = false;
  const dispatchEnter = async (): Promise<void> => {
    if (Date.now() - lastDispatchAt < 1_500) return;
    lastDispatchAt = Date.now();
    try { await sendKeys(session, 'C-m'); } catch { /* keep polling */ }
  };

  while (Date.now() < onboardingDeadline) {
    await sleep(pollIntervalMs);
    const dead = await checkPairPaneAlive(session);
    if (dead) return { kind: 'error', error: dead };
    let pane: string;
    try {
      // VIEWPORT ONLY (no scrollback). With scrollback included, the
      // theme picker / login picker text lingers in the buffer after
      // claude advances to the URL-paste prompt. The match would then
      // dispatch Enter on the empty paste field — submitting an empty
      // code and triggering "OAuth error: Invalid code". Visible-only
      // capture ensures we only react to what's actually on screen.
      pane = await capturePane(session, { scrollback: 0 });
    } catch (err) {
      return { kind: 'error', error: classifyTmuxError(err) };
    }

    // Already at the URL prompt — done driving onboarding, fall through
    // to the URL-extraction loop below.
    if (isUrlPromptReady(pane)) {
      const url = extractOAuthUrl(pane);
      if (url) return { kind: 'url', url };
    }

    // Stuck on "OAuth error: Invalid code. Press Enter to retry" — usually
    // because claude has cached partial state from a previous failed login
    // attempt on this host. Pressing Enter would just resubmit the bad
    // code and loop forever, so bail with an actionable error.
    //
    // Capture WITH scrollback for this check: claude's TUI sometimes
    // re-renders the splash/banner over the OAuth error so the viewport
    // alone misses it (we'd then time out instead of giving the operator
    // an actionable error). OAuth-retry is a sticky state — claude waits
    // for input — so checking recent scrollback is safe; a false positive
    // from stale scrollback would only fire if a previous attempt actually
    // failed, in which case bailing is the right call.
    let scrollPane = pane;
    try {
      scrollPane = await capturePane(session, { scrollback: -50 });
    } catch { /* fall back to viewport-only check */ }
    const hasOAuthInvalidCode = /OAuth error[\s\S]*Invalid code/i.test(scrollPane);
    const hasOAuthRetryPrompt = /OAuth error/i.test(scrollPane) && /Press Enter to retry/i.test(scrollPane);
    if (hasOAuthInvalidCode || hasOAuthRetryPrompt) {
      return {
        kind: 'error',
        error: {
          kind: 'oauth-retry-stuck',
          message:
            'claude is stuck on a previous failed-login retry prompt. SSH to the host and clear ~/.claude/.credentials.json (and any *.json next to it), then retry pair-via-browser.',
        },
      };
    }

    // ENG-10619: the consumer-terms dialog renders a `❯` cursor, so without
    // this the interactive-prompt branch below types `/login` into it.
    // Throttled like dispatchEnter: a relative arrow sequence re-sent before
    // claude re-renders would overshoot the row.
    if (consumerTermsOffKeys(pane)) {
      if (Date.now() - lastDispatchAt >= 1_500) {
        lastDispatchAt = Date.now();
        try {
          await answerConsumerTermsDialog(session, pane);
        } catch (err) {
          return { kind: 'error', error: classifyTmuxError(err) };
        }
      }
      continue;
    }

    // Login method picker — option 1 (Claude subscription) is already
    // highlighted, Enter selects it.
    if (/Select login method:/i.test(pane)) {
      await dispatchEnter();
      continue;
    }
    // First-run theme picker. Accept the highlighted default; operator
    // can change it later via /theme.
    if (/\bDark mode\b/.test(pane) && /\bLight mode\b/.test(pane)) {
      await dispatchEnter();
      continue;
    }
    // "Trust this folder?" — select "Yes, I trust this folder" BY NAME.
    // ENG-10619: this used to be a bare Enter on the assumption the default
    // row is Yes. Since Claude Code 2.1.250 the dialog is an arrow-select
    // list whose cursor defaults to "No, exit", so Enter refused trust and
    // claude exited 1 — every re-login on a host whose workspace was not yet
    // trusted failed (surfaced as "can't find pane" before ENG-10617, and as
    // the trust dialog in the exit output after). Same defect sweepDialogs()
    // fixed for agents in ENG-9575. An unresolvable row sends nothing.
    if (isTrustDialogVisible(pane)) {
      const keys = selectRowKeys(pane, 'Yes, I trust this folder');
      if (keys && Date.now() - lastDispatchAt >= 1_500) {
        lastDispatchAt = Date.now();
        try {
          await sendKeySequence(session, keys);
        } catch (err) {
          return { kind: 'error', error: classifyTmuxError(err) };
        }
      }
      continue;
    }
    // Onboarding "Press Enter to continue" splashes.
    if (/press\s+enter\s+to\s+continue/i.test(pane)) {
      await dispatchEnter();
      continue;
    }
    // Regular interactive prompt — this means claude was already
    // onboarded. Send `/login` once to surface the login picker, then
    // the next iteration handles it via the picker branch above.
    // ENG-5377: detect both the 2.1.146 `❯` cursor + `? for shortcuts`
    // footer AND the legacy boxed/bare prompt shapes. This branch must
    // come AFTER the dialog detectors above, since `❯` ALSO renders on
    // theme/trust/login dialogs.
    if (isInteractivePromptReady(pane) && !loginCommandSent) {
      loginCommandSent = true;
      try {
        await sendKeys(session, '/login', 'C-m');
      } catch (err) {
        return { kind: 'error', error: classifyTmuxError(err) };
      }
      continue;
    }
  }

  // Onboarding deadline hit without seeing a URL — surface the pane so
  // operators can see what was on screen when we gave up.
  let lastPane = '';
  try { lastPane = await capturePane(session); } catch { /* best-effort */ }
  return {
    kind: 'error',
    error: {
      kind: 'unknown',
      message: `claude never reached OAuth URL prompt within ${timeoutMs}ms. Last pane: ${lastPane.slice(-500)}`,
    },
  };
}

// ---------------------------------------------------------------------------
// submit-code — paste the auth code, wait for outcome
// ---------------------------------------------------------------------------

export interface ClaudePairSubmitOpts {
  /** Pair-scoped tmux session name (see pairTmuxSession). */
  session: string;
  code: string;
  /** Total time to wait for the success/failure marker. Default 30s. */
  timeoutMs?: number;
  pollIntervalMs?: number;
}

export async function submitClaudePairCode(
  opts: ClaudePairSubmitOpts,
): Promise<ClaudePairSubmitResult> {
  const { session } = opts;
  const timeoutMs = opts.timeoutMs ?? 30_000;
  const pollIntervalMs = opts.pollIntervalMs ?? 500;

  // Validate code shape minimally — Claude Code's auth codes are
  // alphanumeric with dashes, ~40-80 chars. Accept anything within
  // that envelope; reject blank or whitespace-only to avoid
  // accidentally submitting an empty buffer.
  if (!opts.code || !opts.code.trim()) {
    return {
      kind: 'error',
      error: { kind: 'unknown', message: 'empty auth code' },
    };
  }
  if (opts.code.length > 1024) {
    return {
      kind: 'error',
      error: { kind: 'unknown', message: 'auth code suspiciously long' },
    };
  }

  // Send the code + Enter. We use the literal value as one send-keys
  // argument; tmux handles spaces fine, but newlines would terminate
  // early so reject those as well.
  if (/[\r\n]/.test(opts.code)) {
    return {
      kind: 'error',
      error: { kind: 'unknown', message: 'auth code contains newline' },
    };
  }

  // Send the code as LITERAL text (-l) so tmux doesn't try to interpret
  // any chars as key tokens. Then a 250ms breather so claude's
  // ink/React render loop finishes processing the paste before Enter
  // lands. Without that wait the Enter often gets swallowed mid-render
  // and the code stays in the input box unsubmitted.
  try {
    await execFileAsync('tmux', ['send-keys', '-t', session, '-l', opts.code.trim()]);
  } catch (err) {
    return { kind: 'error', error: classifyTmuxError(err) };
  }
  await sleep(250);
  try {
    // Use Enter (semantic) plus a fallback C-m on the next iteration if
    // claude still hasn't moved.
    await sendKeys(session, 'Enter');
  } catch (err) {
    return { kind: 'error', error: classifyTmuxError(err) };
  }

  const deadline = Date.now() + timeoutMs;
  let enterRetried = false;
  let lastPane = '';
  while (Date.now() < deadline) {
    await sleep(pollIntervalMs);
    try {
      lastPane = await capturePane(session);
    } catch (err) {
      return { kind: 'error', error: classifyTmuxError(err) };
    }
    const outcome: AuthOutcome = detectAuthOutcome(lastPane);
    if (outcome.kind === 'success') return { kind: 'success', rawMatch: outcome.rawMatch };
    if (outcome.kind === 'failure') return { kind: 'failure', rawMatch: outcome.rawMatch };
    // ENG-10617: checked after the outcome so a success/failure marker
    // on claude's final screen still wins over "it exited".
    const dead = await checkPairPaneAlive(session);
    if (dead) return { kind: 'error', error: dead };

    // After ~5s with no outcome, retry Enter once — covers the case
    // where the first Enter landed mid-render and claude swallowed it.
    // The "Paste code here" prompt is still on screen if submission
    // didn't take; if it advanced, we'd already have an outcome above.
    if (!enterRetried && Date.now() - (deadline - timeoutMs) > 5_000 && /Paste code here/i.test(lastPane)) {
      enterRetried = true;
      try { await sendKeys(session, 'C-m'); } catch { /* keep polling */ }
    }
  }
  // Include pane snippet in the error path so operators can see what
  // claude was actually showing — outcome detection is brittle and the
  // session is killed immediately after this returns, so this is our
  // only chance to capture state for debugging regex updates.
  return {
    kind: 'error',
    error: {
      kind: 'unknown',
      message: `submit timed out after ${timeoutMs}ms — outcome regex didn't match. Last pane: ${lastPane.slice(-600)}`,
    },
  };
}

// ---------------------------------------------------------------------------
// status — non-mutating peek at the pane state
// ---------------------------------------------------------------------------

export async function getClaudePairStatus(session: string): Promise<ClaudePairStatusResult> {
  const dead = await checkPairPaneAlive(session);
  if (dead?.kind === 'no-session') return { kind: 'session-missing' };
  if (dead) return { kind: 'error', error: dead };

  let pane: string;
  try {
    pane = await capturePane(session);
  } catch (err) {
    return { kind: 'error', error: classifyTmuxError(err) };
  }

  // Outcome takes priority — if the pane already shows success/failure
  // from a recent submission, the API can short-circuit without
  // restarting the flow.
  const outcome = detectAuthOutcome(pane);
  if (outcome.kind === 'success') return { kind: 'success' };
  if (outcome.kind === 'failure') return { kind: 'failure', rawMatch: outcome.rawMatch };

  if (isUrlPromptReady(pane)) {
    const url = extractOAuthUrl(pane);
    if (url) return { kind: 'awaiting-code', url };
  }
  return { kind: 'idle' };
}
